import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, CalendarDaysIcon, GlobeIcon, BuildingIcon, PackageIcon, UserIcon, MonitorIcon, FilterIcon, XIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RESOURCES, USAGE_TYPES, BOOKING_FILES } from '../../data/windsor';
import { toCalendarEvents, monthGrid, isoDate, usageTint, compactTime, formatTime, findUsageType, resourcePath, type CalendarEvent } from './bookingHelpers';
import type { BookingFile, ResourceKind } from '../../types/loisirs';
interface Props {
  /** Opens the booking file behind an event. */
  onOpenBooking?: (booking: BookingFile) => void;
}
type View = 'mois' | 'semaine' | 'jour';
const OPEN_HOUR = 6;
const CLOSE_HOUR = 23;
const HOUR_HEIGHT = 44;
const KIND_ICON: Record<ResourceKind, typeof BuildingIcon> = {
  plateau: BuildingIcon,
  equipement: PackageIcon,
  humain: UserIcon,
  enligne: MonitorIcon
};
export function ResourceCalendar({
  onOpenBooking
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const fr = lang === 'fr';
  const locale = fr ? 'fr-CA' : 'en-CA';
  const [view, setView] = useState<View>('mois');
  /** Anchored on September 2026, the busiest month of the season. */
  const [cursor, setCursor] = useState(new Date(2026, 8, 15));
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [publicOnly, setPublicOnly] = useState(false);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const bookableIds = useMemo(() => RESOURCES.filter(r => r.bookable).map(r => r.id), []);
  const [selectedResources, setSelectedResources] = useState<string[]>(bookableIds);
  const [selectedUsages, setSelectedUsages] = useState<string[]>(USAGE_TYPES.map(u => u.id));
  /** Single resource shown in the week and day grids. */
  const [focusResource, setFocusResource] = useState('res-lemay-glace');
  const parents = useMemo(() => RESOURCES.filter(r => !r.parentId), []);
  const childrenOf = (id: string) => RESOURCES.filter(r => r.parentId === id);
  const events = useMemo(() => {
    const all = toCalendarEvents(BOOKING_FILES);
    return all.filter(e => {
      if (!selectedResources.includes(e.booking.resourceId)) return false;
      if (!selectedUsages.includes(e.booking.usageTypeId)) return false;
      if (publicOnly) {
        const usage = findUsageType(e.booking.usageTypeId);
        if (!e.booking.publicCalendar || !usage?.showOnPublicCalendar) {
          return false;
        }
      }
      return true;
    });
  }, [selectedResources, selectedUsages, publicOnly]);
  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach(e => {
      const list = map.get(e.date) ?? [];
      list.push(e);
      map.set(e.date, list);
    });
    map.forEach(list => list.sort((a, b) => a.startTime.localeCompare(b.startTime)));
    return map;
  }, [events]);

  // ---------------------------------------------------------------- HEADER
  const periodTitle = () => {
    if (view === 'mois') {
      const label = cursor.toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric'
      });
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
    if (view === 'jour') {
      return cursor.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    const start = new Date(cursor);
    start.setDate(cursor.getDate() - cursor.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short'
    })} – ${end.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })}`;
  };
  const step = (direction: 1 | -1) => {
    const next = new Date(cursor);
    if (view === 'mois') next.setMonth(cursor.getMonth() + direction);else if (view === 'semaine') next.setDate(cursor.getDate() + direction * 7);else next.setDate(cursor.getDate() + direction);
    setCursor(next);
    setExpandedDay(null);
  };
  const toggleResource = (id: string) => {
    const kids = childrenOf(id).map(c => c.id);
    const group = [id, ...kids].filter(rid => RESOURCES.find(r => r.id === rid && r.bookable));
    const allOn = group.every(rid => selectedResources.includes(rid));
    setSelectedResources(prev => allOn ? prev.filter(rid => !group.includes(rid)) : Array.from(new Set([...prev, ...group])));
  };
  const weekDayNames = useMemo(() => {
    const base = new Date(2026, 8, 6); // a Sunday
    return Array.from({
      length: 7
    }, (_, i) => {
      const day = new Date(base);
      day.setDate(base.getDate() + i);
      return day.toLocaleDateString(locale, {
        weekday: 'short'
      });
    });
  }, [locale]);

  // ----------------------------------------------------------- EVENT PILL
  const EventPill = ({
    event,
    dense
  }: {
    event: CalendarEvent;
    dense?: boolean;
  }) => {
    const usage = findUsageType(event.booking.usageTypeId);
    if (!usage) return null;
    const tint = usageTint(usage.color);
    if (event.allDay) {
      return <button onClick={() => onOpenBooking?.(event.booking)} title={`${usage.label[lang]} · ${event.booking.client.name}`} className="w-full flex items-center gap-1.5 rounded px-1.5 py-0.5 text-left transition-opacity hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-[#4F46E5]" style={{
        backgroundColor: `${usage.color}1F`
      }} data-id="element-3167" data-mp="1g39dcal1dlks">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
          backgroundColor: usage.color
        }} data-id="element-3168" data-mp="x4f1b9cyiz8b" />
          <span className="text-[10px] font-semibold truncate" style={{
          color: usage.color
        }} data-id="element-3169" data-mp="3vtcg4hhndx69">
            {usage.label[lang]}
          </span>
        </button>;
    }
    return <button onClick={() => onOpenBooking?.(event.booking)} title={`${usage.label[lang]} · ${formatTime(event.startTime, fr)} – ${formatTime(event.endTime, fr)} · ${event.booking.client.name}`} className="w-full rounded-md border px-1.5 py-1 text-left transition-shadow hover:shadow-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5]" style={tint} data-id="element-3170" data-mp="17wdqqgha4p5y">
        <span className="flex items-center gap-1" data-id="element-3171" data-mp="k0sz0t1b598l">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
          backgroundColor: usage.color
        }} data-id="element-3172" data-mp="k2420qmxicyn" />
          <span className="text-[10px] font-medium tabular-nums opacity-80" data-id="element-3173" data-mp="t1hd1nqpb9lh">
            {compactTime(event.startTime, fr)}
          </span>
        </span>
        <span className="block text-[11px] font-bold leading-tight truncate" data-id="element-3174" data-mp="29dwhn6verc9t">
          {usage.label[lang]}
        </span>
        {!dense && <span className="block text-[10px] leading-tight text-slate-500 truncate" data-id="element-3175" data-mp="2cxn1ts528dq8">
            {resourcePath(event.booking.resourceId, lang)}
          </span>}
      </button>;
  };

  // ------------------------------------------------------------ MONTH VIEW
  const MonthView = () => {
    const days = monthGrid(cursor.getFullYear(), cursor.getMonth());
    return <>
        {/* Desktop grid */}
        <div className="hidden md:block" data-id="element-3176" data-mp="2vnmoy9hcbir0">
          <div className="grid grid-cols-7 border-b border-slate-200" data-id="element-3177" data-mp="2vpzgnecwh7h9">
            {weekDayNames.map(name => <div key={name} className="px-2 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-3178" data-mp="30oslyji19s6c">
                {name}
              </div>)}
          </div>
          <div className="grid grid-cols-7" data-id="element-3179" data-mp="3elpi90gx81um">
            {days.map(day => {
            const key = isoDate(day);
            const dayEvents = byDate.get(key) ?? [];
            const allDayEvents = dayEvents.filter(e => e.allDay);
            const timed = dayEvents.filter(e => !e.allDay);
            const outside = day.getMonth() !== cursor.getMonth();
            const today = key === '2026-09-15';
            const expanded = expandedDay === key;
            const shown = expanded ? timed : timed.slice(0, 2);
            return <div key={key} className={`min-h-[110px] border-b border-r border-slate-100 p-1.5 ${outside ? 'bg-slate-50/60' : 'bg-white'}`} data-id="element-3180" data-mp="22a3m0eql1j65">
                  <div className="flex items-center justify-between mb-1" data-id="element-3181" data-mp="1t8cgb9w1lcca">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${today ? 'bg-[#4F46E5] text-white' : outside ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-3182" data-mp="1oau49amqah02">
                      {day.getDate()}
                    </span>
                    {dayEvents.length > 0 && <span className="text-[10px] text-slate-400 tabular-nums" data-id="element-3183" data-mp="2xi4co4b54ft7">
                        {dayEvents.length}
                      </span>}
                  </div>

                  {allDayEvents.length > 0 && <div className="space-y-0.5 mb-1" data-id="element-3184" data-mp="37bcsfd3f4jk3">
                      {allDayEvents.map(e => <EventPill key={e.key} event={e} dense data-id="element-3185" data-mp="3h76epo6xvrf2" />)}
                    </div>}

                  <div className="space-y-1" data-id="element-3186" data-mp="3l91dakxhjqp5">
                    {shown.map(e => <EventPill key={e.key} event={e} dense data-id="element-3187" data-mp="1mtneu7pfrwql" />)}
                  </div>

                  {timed.length > 2 && <button onClick={() => setExpandedDay(expanded ? null : key)} className="mt-1 text-[10px] font-semibold text-[#4F46E5] hover:underline" data-id="element-3188" data-mp="1qwtf8ypvatp5">
                      {expanded ? t('Réduire', 'Show less') : t(`+${timed.length - 2} autres`, `+${timed.length - 2} more`)}
                    </button>}
                </div>;
          })}
          </div>
        </div>

        {/* Mobile list */}
        <div className="md:hidden divide-y divide-slate-100" data-id="element-3189" data-mp="gea9b6nevaay">
          {days.filter(day => day.getMonth() === cursor.getMonth() && (byDate.get(isoDate(day)) ?? []).length > 0).map(day => {
          const key = isoDate(day);
          const dayEvents = byDate.get(key) ?? [];
          return <div key={key} className="px-4 py-3" data-id="element-3190" data-mp="2ftuz9valti21">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2" data-id="element-3191" data-mp="2fums4ycth1om">
                    {day.toLocaleDateString(locale, {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
                  </p>
                  <div className="space-y-1.5" data-id="element-3192" data-mp="20y5hyagd5t3n">
                    {dayEvents.map(e => <EventPill key={e.key} event={e} data-id="element-3193" data-mp="2ylw3ld65e0iu" />)}
                  </div>
                </div>;
        })}
          {events.length === 0 && <EmptyState data-id="element-3194" data-mp="2osp0jyyvqwlx" />}
        </div>
      </>;
  };

  // ------------------------------------------------------- WEEK / DAY VIEW
  const TimeGrid = ({
    dayCount
  }: {
    dayCount: 1 | 7;
  }) => {
    const hours = Array.from({
      length: CLOSE_HOUR - OPEN_HOUR
    }, (_, i) => OPEN_HOUR + i);
    const start = new Date(cursor);
    if (dayCount === 7) start.setDate(cursor.getDate() - cursor.getDay());
    const days = Array.from({
      length: dayCount
    }, (_, i) => {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      return day;
    });
    return <div className="overflow-x-auto" data-id="element-3195" data-mp="10v7klkm00z6v">
        <div className={dayCount === 7 ? 'min-w-[780px]' : ''} data-id="element-3196" data-mp="10u4sj9cxwqpd">
          <div className="grid gap-px" style={{
          gridTemplateColumns: `56px repeat(${dayCount}, minmax(0,1fr))`
        }} data-id="element-3197" data-mp="15u0xbatgom5z">
            <div data-id="element-3198" data-mp="vxed8o8ylz50" />
            {days.map(day => <div key={isoDate(day)} className="px-2 py-2 text-center bg-slate-50 border border-slate-200 rounded-t-md" data-id="element-3199" data-mp="vv1mpmp4yggc">
                <p className="text-xs font-semibold text-slate-700" data-id="element-3200" data-mp="mx98ymz2bgv2">
                  {day.toLocaleDateString(locale, {
                weekday: 'short'
              })}
                </p>
                <p className="text-[11px] text-slate-400" data-id="element-3201" data-mp="2hb0o2205usxw">{day.getDate()}</p>
              </div>)}
          </div>

          <div className="grid gap-px" style={{
          gridTemplateColumns: `56px repeat(${dayCount}, minmax(0,1fr))`
        }} data-id="element-3202" data-mp="5o6u8ah0bza7">
            <div data-id="element-3203" data-mp="fdgre4qpk856">
              {hours.map(h => <div key={h} style={{
              height: HOUR_HEIGHT
            }} className="pr-2 text-right text-[11px] text-slate-400 tabular-nums -translate-y-1.5" data-id="element-3204" data-mp="ff1j7mln1czc">
                  {formatTime(`${String(h).padStart(2, '0')}:00`, fr)}
                </div>)}
            </div>

            {days.map(day => {
            const key = isoDate(day);
            const dayEvents = (byDate.get(key) ?? []).filter(e => e.booking.resourceId === focusResource);
            return <div key={key} className="relative border border-slate-200 bg-white" style={{
              height: hours.length * HOUR_HEIGHT
            }} data-id="element-3205" data-mp="303fv220pzv1z">
                  {hours.map((h, i) => <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{
                top: i * HOUR_HEIGHT
              }} data-id="element-3206" data-mp="1r5psm4g5cg0t" data-mpi={i} />)}
                  {dayEvents.map(e => {
                const usage = findUsageType(e.booking.usageTypeId);
                if (!usage) return null;
                const from = e.allDay ? OPEN_HOUR : Math.max(OPEN_HOUR, Number(e.startTime.split(':')[0]) + Number(e.startTime.split(':')[1]) / 60);
                const to = e.allDay ? CLOSE_HOUR : Math.min(CLOSE_HOUR, Number(e.endTime.split(':')[0]) + Number(e.endTime.split(':')[1]) / 60);
                return <button key={e.key} onClick={() => onOpenBooking?.(e.booking)} className="absolute left-1 right-1 rounded-md border px-1.5 py-1 overflow-hidden text-left focus:outline-none focus:ring-1 focus:ring-[#4F46E5]" style={{
                  ...usageTint(usage.color),
                  top: (from - OPEN_HOUR) * HOUR_HEIGHT + 1,
                  height: Math.max((to - from) * HOUR_HEIGHT - 2, 20)
                }} data-id="element-3207" data-mp="ca7ng0nosqg9">
                        <span className="block text-[11px] font-bold leading-tight truncate" data-id="element-3208" data-mp="3go5xyxp570d7">
                          {usage.label[lang]}
                        </span>
                        <span className="block text-[10px] leading-tight opacity-70 truncate" data-id="element-3209" data-mp="36pgnhpzksi6z">
                          {e.allDay ? t('Toute la journée', 'All day') : `${formatTime(e.startTime, fr)} – ${formatTime(e.endTime, fr)}`}
                        </span>
                        <span className="block text-[10px] leading-tight text-slate-500 truncate" data-id="element-3210" data-mp="2n46o5ptzhnco">
                          {e.booking.client.name}
                        </span>
                      </button>;
              })}
                </div>;
          })}
          </div>
        </div>
      </div>;
  };
  const EmptyState = () => <div className="py-16 text-center" data-id="element-3211" data-mp="3629afwv5d66s">
      <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3" data-id="element-3212" data-mp="361hn19bpeerl">
        <CalendarDaysIcon className="w-6 h-6 text-slate-400" data-id="element-3213" data-mp="3b01877ih3miw" />
      </div>
      <p className="text-sm font-semibold text-slate-700" data-id="element-3214" data-mp="2laiz1pdmfia">
        {t('Aucune réservation à afficher', 'No booking to display')}
      </p>
      <p className="text-xs text-slate-500 mt-1" data-id="element-3215" data-mp="7dk5e7ra8xft">
        {t('Ajustez les filtres de ressources ou de types d’utilisation.', 'Adjust the resource or usage type filters.')}
      </p>
    </div>;
  return <div className="space-y-4" data-id="element-3216" data-mp="21s7rzpy4kfpg">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between" data-id="element-3217" data-mp="21ry2s4kwmckt">
        <div className="flex items-center gap-2" data-id="element-3218" data-mp="1wufnzxk7h34w">
          <h3 className="text-lg font-bold text-[#0F172A] font-heading min-w-[180px]" data-id="element-3219" data-mp="1wvqtq2x7okyn">
            {periodTitle()}
          </h3>
          <div className="flex items-center gap-1" data-id="element-3220" data-mp="1458q90skshkp">
            <button onClick={() => step(-1)} aria-label={t('Période précédente', 'Previous period')} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" data-id="element-3221" data-mp="146jxjal8c7gk">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-3222" data-mp="2tim015jgixt7" />
            </button>
            <button onClick={() => step(1)} aria-label={t('Période suivante', 'Next period')} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" data-id="element-3223" data-mp="2pibtnrrr65dt">
              <ChevronRightIcon className="w-4 h-4" data-id="element-3224" data-mp="ruzomflwaozh" />
            </button>
            <button onClick={() => setCursor(new Date(2026, 8, 15))} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-3225" data-mp="wsjao1ou461z">
              {t('Aujourd’hui', 'Today')}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-3226" data-mp="1aj4lr481rbpb">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-3227" data-mp="1fjiwiu118zr1">
            {[{
            id: 'mois' as View,
            label: t('Mois', 'Month')
          }, {
            id: 'semaine' as View,
            label: t('Semaine', 'Week')
          }, {
            id: 'jour' as View,
            label: t('Jour', 'Day')
          }].map(option => <button key={option.id} onClick={() => setView(option.id)} className={`px-3.5 py-2 text-sm font-medium transition-colors ${view === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3228" data-mp="23fdt1nky65yq">
                {option.label}
              </button>)}
          </div>
          <button onClick={() => setFiltersOpen(v => !v)} aria-expanded={filtersOpen} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border text-sm font-medium transition-colors ${filtersOpen ? 'border-[#4F46E5] bg-indigo-50 text-[#4F46E5]' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3229" data-mp="2mdq2zbjonjvd">
            <FilterIcon className="w-4 h-4" data-id="element-3230" data-mp="wzbhr17vif4m" />
            {t('Filtres', 'Filters')}
          </button>
        </div>
      </div>

      <div className="flex gap-4 items-start" data-id="element-3231" data-mp="18myjczvzxijn">
        {/* Filter panel */}
        {filtersOpen && <aside className="hidden lg:block w-72 shrink-0 bg-white rounded-xl border border-slate-200 p-4 space-y-5" data-id="element-3232" data-mp="13vgjkf5plo09">
            <div className="flex items-center justify-between" data-id="element-3233" data-mp="yxf4e5bem8u2">
              <h4 className="text-sm font-bold text-[#0F172A]" data-id="element-3234" data-mp="ty2h5jgmezqy">
                {t('Filtres', 'Filters')}
              </h4>
              <button onClick={() => setFiltersOpen(false)} aria-label={t('Fermer les filtres', 'Close filters')} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-3235" data-mp="1rk9lqtcxcs3b">
                <XIcon className="w-4 h-4" data-id="element-3236" data-mp="1cojuafoyzmr7" />
              </button>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-id="element-3237" data-mp="67yaf384yjvq">
              <button type="button" role="switch" aria-checked={publicOnly} onClick={() => setPublicOnly(v => !v)} className={`mt-0.5 relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${publicOnly ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3238" data-mp="65vddz8kfqhi">
                <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${publicOnly ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-3239" data-mp="2rmfr0icwi6ek" />
              </button>
              <span data-id="element-3240" data-mp="35itbm0cqenx9">
                <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800" data-id="element-3241" data-mp="3ajqpi0qpkvsy">
                  <GlobeIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3242" data-mp="1b74pne831l3d" />
                  {t('Afficher le public seulement', 'Show public only')}
                </span>
                <span className="block text-xs text-slate-500 mt-0.5" data-id="element-3243" data-mp="1g67vv1zstxcx">
                  {t('Masque les blocs internes comme l’arrosage de glace.', 'Hides internal blocks such as ice resurfacing.')}
                </span>
              </span>
            </label>

            {/* Resource tree */}
            <div data-id="element-3244" data-mp="1d43d2v6gxv72">
              <div className="flex items-center justify-between mb-2" data-id="element-3245" data-mp="1d3lazhx4kmr8">
                <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-3246" data-mp="1r3mdf1t1g28t">
                  {t('Ressources', 'Resources')}
                </h5>
                <button onClick={() => setSelectedResources(selectedResources.length === bookableIds.length ? [] : bookableIds)} className="text-xs font-semibold text-[#4F46E5] hover:underline" data-id="element-3247" data-mp="1n0pypm6mcqa5">
                  {selectedResources.length === bookableIds.length ? t('Aucune', 'None') : t('Toutes', 'All')}
                </button>
              </div>
              <ul className="max-h-72 overflow-y-auto pr-1 space-y-0.5" data-id="element-3248" data-mp="2tfvubi3sp4md">
                {parents.map(parent => {
              const kids = childrenOf(parent.id);
              const Icon = KIND_ICON[parent.kind];
              const group = [parent.id, ...kids.map(k => k.id)].filter(id => RESOURCES.find(r => r.id === id && r.bookable));
              const checked = group.length > 0 && group.every(id => selectedResources.includes(id));
              const partial = !checked && group.some(id => selectedResources.includes(id));
              return <li key={parent.id} data-id="element-3249" data-mp="1mu1y2e4kdku5">
                      <div className="flex items-center gap-2 py-1" data-id="element-3250" data-mp="1rrl9755ythhv">
                        <button onClick={() => toggleResource(parent.id)} role="checkbox" aria-checked={checked} aria-label={parent.name[lang]} className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : partial ? 'bg-indigo-100 border-[#4F46E5]' : 'bg-white border-slate-300'}`} data-id="element-3251" data-mp="1rswcz4zeup8z">
                          {checked && <CheckIcon className="w-3 h-3" data-id="element-3252" data-mp="1xeqnobgmuk6k" />}
                          {partial && <span className="w-1.5 h-0.5 bg-[#4F46E5] rounded" data-id="element-3253" data-mp="mgprce7gsl4" />}
                        </button>
                        {kids.length > 0 ? <ChevronDownIcon className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" data-id="element-3254" data-mp="36mtpk2tkj1o" /> : <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-3255" data-mp="84emegbag96x" />}
                        <span className="text-xs font-medium text-slate-700 truncate" data-id="element-3256" data-mp="cxhfyog2fin4">
                          {parent.name[lang]}
                        </span>
                      </div>
                      {kids.length > 0 && <ul className="ml-6 space-y-0.5" data-id="element-3257" data-mp="3gf9fcnwj74vc">
                          {kids.map(kid => <li key={kid.id} className="flex items-center gap-2 py-1" data-id="element-3258" data-mp="3qb2oryeyei8w">
                              <button onClick={() => setSelectedResources(prev => prev.includes(kid.id) ? prev.filter(id => id !== kid.id) : [...prev, kid.id])} role="checkbox" aria-checked={selectedResources.includes(kid.id)} aria-label={kid.name[lang]} className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selectedResources.includes(kid.id) ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : 'bg-white border-slate-300'}`} data-id="element-3259" data-mp="3v82w0cnjhr1l">
                                {selectedResources.includes(kid.id) && <CheckIcon className="w-3 h-3" data-id="element-3260" data-mp="246roh2ve9nz4" />}
                              </button>
                              <span className="text-xs text-slate-600 truncate" data-id="element-3261" data-mp="1q3cab7qov8cu">
                                {kid.name[lang]}
                              </span>
                            </li>)}
                        </ul>}
                    </li>;
            })}
              </ul>
            </div>

            {/* Usage types */}
            <div data-id="element-3262" data-mp="28t0se09fyuas">
              <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-3263" data-mp="28ulnbqzo4f96">
                {t('Types d’utilisation', 'Usage types')}
              </h5>
              <ul className="space-y-0.5 max-h-56 overflow-y-auto pr-1" data-id="element-3264" data-mp="1yvmkip1l3du3">
                {USAGE_TYPES.map(usage => {
              const on = selectedUsages.includes(usage.id);
              return <li key={usage.id} data-id="element-3265" data-mp="nfxz423zamve">
                      <button onClick={() => setSelectedUsages(prev => prev.includes(usage.id) ? prev.filter(id => id !== usage.id) : [...prev, usage.id])} role="checkbox" aria-checked={on} className="w-full flex items-center gap-2 py-1 text-left" data-id="element-3266" data-mp="newa0tq4nzuo">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${on ? 'text-white' : 'bg-white border-slate-300'}`} style={on ? {
                    backgroundColor: usage.color,
                    borderColor: usage.color
                  } : undefined} data-id="element-3267" data-mp="sezj33mwropk">
                          {on && <CheckIcon className="w-3 h-3" data-id="element-3268" data-mp="2p2tqbbwep9jb" />}
                        </span>
                        <span className="text-xs text-slate-600 truncate" data-id="element-3269" data-mp="2u03c8hkl732e">
                          {usage.label[lang]}
                        </span>
                        {!usage.showOnPublicCalendar && <span className="ml-auto text-[10px] text-slate-400 shrink-0" data-id="element-3270" data-mp="332masphcatiu">
                            {t('interne', 'internal')}
                          </span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>
          </aside>}

        {/* Calendar surface */}
        <div className="flex-1 min-w-0 space-y-3" data-id="element-3271" data-mp="3lsarpeocw9f7">
          {view !== 'mois' && <div className="flex items-center gap-2 flex-wrap" data-id="element-3272" data-mp="3qrnq8wolb4xm">
              <label htmlFor="focus-resource" className="text-xs font-medium text-slate-600" data-id="element-3273" data-mp="3qp19c6ft7d2q">
                {t('Ressource affichée', 'Displayed resource')}
              </label>
              <select id="focus-resource" value={focusResource} onChange={e => setFocusResource(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3274" data-mp="14utopgeguvjo">
                {RESOURCES.filter(r => r.bookable).map(r => <option key={r.id} value={r.id} data-id="element-3275" data-mp="1nlt69wjmt32x">
                    {resourcePath(r.id, lang)}
                  </option>)}
              </select>
            </div>}

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3276" data-mp="1esptzrkq2w9v">
            {view === 'mois' ? <MonthView data-id="element-3277" data-mp="22i22ep8870b4" /> : <div className="p-4" data-id="element-3278" data-mp="22gzbwietezvq">
                <TimeGrid dayCount={view === 'semaine' ? 7 : 1} data-id="element-3279" data-mp="279sgn6aaa87t" />
              </div>}
          </div>

          {/* Legend */}
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-3280" data-mp="2gmj962pzql2v">
            {USAGE_TYPES.filter(u => selectedUsages.includes(u.id)).map(usage => <li key={usage.id} className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-id="element-3281" data-mp="2qcucpo5ris98">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{
              backgroundColor: usage.color
            }} data-id="element-3282" data-mp="1xp88cdopnjlb" />
                  {usage.label[lang]}
                </li>)}
          </ul>
        </div>
      </div>
    </div>;
}